import { Calendar, Users, MapPin, Shield, Bus, UserCheck } from "lucide-react";

const ServiceFeatures = () => {
  const features = [
    {
      icon: <Calendar className="w-8 h-8 text-[#F39800]" />,
      title: "원하는 날짜에 떠나는 자유로운 예약",
      description: "다른 사이트는 정해진 상품만 예약 가능하지만, 홀씨는 고객이 직접 원하는 코스와 날짜를 선택할 수 있습니다.",
      highlight: "고객 맞춤형 예약 시스템"
    },
    {
      icon: <Users className="w-8 h-8 text-[#F39800]" />,
      title: "최소 인원 충족시 확정 출발",
      description: "투명한 출발 기준으로 최소 인원만 모이면 바로 출발합니다. 명확하고 신뢰할 수 있는 예약 시스템입니다.",
      highlight: "투명한 출발 기준"
    },
    {
      icon: <Bus className="w-8 h-8 text-[#F39800]" />,
      title: "프리미엄 리무진 버스 서비스",
      description: "편안한 리클라이닝 시트와 넓은 공간으로 이동 중에도 편안하게 휴식하며 목적지까지 안전하게 이동합니다.",
      highlight: "편안한 이동과 휴식"
    },
    {
      icon: <UserCheck className="w-8 h-8 text-[#F39800]" />,
      title: "전문 인솔자 동행",
      description: "경험 많은 전문 인솔자가 함께하여 안전하고 알찬 트레킹을 도와드립니다. 코스 안내부터 안전 관리까지 책임집니다.",
      highlight: "안전하고 알찬 가이드"
    },
    {
      icon: <Shield className="w-8 h-8 text-[#F39800]" />,
      title: "안전한 당일 투어",
      description: "당일 출발하여 당일 복귀하는 안전한 일정으로, 무리하지 않는 적당한 코스만을 선별하여 제공합니다.",
      highlight: "무리하지 않는 적당한 코스"
    },
    {
      icon: <MapPin className="w-8 h-8 text-[#F39800]" />,
      title: "전국 코리아둘레길 완전 정복",
      description: "해파랑길, 남파랑길, 서해랑길, DMZ 평화의 길까지 전국의 모든 둘레길을 홀씨와 함께 경험해보세요.",
      highlight: "전국 4,500km 둘레길"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#F39800]/10 text-[#F39800] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <span>✨</span>
            <span>홀씨만의 특별한 서비스</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            다른 곳은 정해진 상품만,
            <span className="block text-[#F39800] mt-2">홀씨는 원하는 날짜에!</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            기존의 고정된 일정에 맞춰야 했던 불편함은 이제 그만! <br />
            홀씨와 함께라면 언제든 원하는 날짜에 떠날 수 있습니다.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-[#F39800]/10 rounded-xl">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 leading-tight">
                    {feature.title}
                  </h3>
                </div>
              </div>
              
              <p className="text-gray-600 leading-relaxed mb-4">
                {feature.description}
              </p>
              
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F39800]/20 to-[#F39800]/10 text-[#F39800] px-3 py-1 rounded-full text-sm font-medium">
                <span className="w-2 h-2 bg-[#F39800] rounded-full"></span>
                <span>{feature.highlight}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/50 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              지금 바로 원하는 날짜에 예약하세요!
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              홀씨만의 혁신적인 예약 시스템으로 언제든 원하는 코스와 날짜를 선택할 수 있습니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center gap-2 text-[#F39800] font-medium">
                <MapPin className="w-5 h-5" />
                <span>원하는 코스 선택</span>
              </div>

              <span className="hidden sm:block text-gray-400">→</span>
              <div className="flex items-center gap-2 text-[#F39800] font-medium">
                <Calendar className="w-5 h-5" />
                <span>원하는 날짜 선택</span>
              </div>

              <span className="hidden sm:block text-gray-400">→</span>
              <div className="flex items-center gap-2 text-[#F39800] font-medium">
                <Users className="w-5 h-5" />
                <span>최소 인원 충족시 출발</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceFeatures;